import {
  Alert,
  Box,
  Form,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import MoneyField from 'modules/core/components/MoneyField'
import { track } from 'modules/core/lib/Tracking'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isNumber } from 'modules/core/lib/validateNumberInput'

import { useCreateBudget } from 'modules/finance/hooks/useBudgets'
import { events } from 'modules/finance/lib/events'
import {
  WizardData,
  WizardDataField,
  validateMandatoryFIels,
} from 'modules/finance/lib/newBudgetWizard'

const BudgetAmounts = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const { createBudget } = useCreateBudget()

  const handleSubmit = async (formData: WizardData) => {
    // Validate mandatory fields
    const mandatoryFields: WizardDataField[] = ['effectiveFrom', 'amountCents']
    const { invalidFields, dataWithErrors } = validateMandatoryFIels(mandatoryFields, formData)

    onDataChanged(dataWithErrors)

    if (invalidFields.length) {
      return Promise.reject()
    }

    // Validate amount
    if (!isNumber(formData.amountCents) || Number(formData.amountCents) <= 0) {
      onDataChanged({
        ...dataWithErrors,
        errors: {
          amountCents: i18n.t('budgets.forms.errors.invalid_amount'),
        },
      })
      return Promise.reject()
    }

    // Validate dates
    if (
      formData.effectiveUntil &&
      moment(formData.effectiveFrom).isAfter(moment(formData.effectiveUntil))
    ) {
      onDataChanged({
        ...dataWithErrors,
        errors: {
          effectiveFrom: i18n.t('budgets.forms.errors.start_date'),
        },
      })
      return Promise.reject()
    }

    if (!formData.legalEntityId) {
      return Promise.reject()
    }

    const budgetAmount = Number(formData.amountCents)

    // Create budget with periodicity set to 'none'
    // Training class budgets don't have spenders, so pass empty array
    const response = await createBudget({
      name: formData.name,
      description: formData.description,
      legalEntityId: formData.legalEntityId,
      amountCents: toCents(budgetAmount),
      effectiveFrom: formData.effectiveFrom,
      effectiveUntil: formData.effectiveUntil,
      periodicity: 'none',
      ownerEmployeeIds: formData.ownerEmployeeIds,
      viewerEmployeeIds: formData.viewerEmployeeIds,
      spenderEmployeeIds: [],
      budgetableBy: formData.budgetableBy,
    })

    const result = handleResult(response, (formErrors) => {
      onDataChanged({
        ...formData,
        success: false,
        errors: formErrors,
      })
    })

    if (result.success) {
      track(events.completeAddBudget, { budgetId: result.data.budget?.id })
      onDataChanged({
        ...formData,
        success: true,
        budgetId: result.data.budget?.id,
      })
    }

    return response
  }

  useOnNext(handleSubmit)
  return (
    <Box gap="s40" flexDirection="column">
      <Box gap="s24" width="full">
        <Box gap="s8">
          <Text.Header2>{i18n.t('budgets.forms.new_budget.budget_amounts.title')}</Text.Header2>

          <Text.Normal>{i18n.t('budgets.forms.new_budget.budget_amounts.description')}</Text.Normal>
        </Box>
      </Box>
      <Box flexDirection="row" gap="s24" justifyContent="spaceBetween">
        <Box gap="s40" maxWidth="s600">
          <Form padding="s0" notRounded>
            <Box style={{ overflowWrap: 'normal' }}>
              <MoneyField
                error={data.errors?.amountCents}
                mandatory
                currency={data.currency}
                label={i18n.t('budgets.forms.new_budget.budget_amounts.amount.title')}
                value={data.amountCents}
                description={i18n.t('budgets.forms.new_budget.budget_amounts.amount.description')}
                placeholder="0"
                onChange={(value) => onDataChanged({ ...data, amountCents: value })}
              />
            </Box>
            <Box flexDirection="row" gap="s16">
              <DayPicker
                mandatory
                valueFormat="backend_datetime"
                mode="single"
                error={data.errors?.effectiveFrom}
                value={data.effectiveFrom}
                placeholder={i18n.t(
                  'budgets.forms.new_budget.budget_limits.start_date_placeholder'
                )}
                onChange={(value) => onDataChanged({ ...data, effectiveFrom: value })}
                label={i18n.t('budgets.forms.new_budget.budget_limits.start_date')}
              />
              <DayPicker
                valueFormat="backend_datetime"
                mode="single"
                value={data.effectiveUntil}
                minDate={moment(data.effectiveFrom).startOf('day')}
                onChange={(value) => onDataChanged({ ...data, effectiveUntil: value })}
                label={i18n.t('budgets.forms.new_budget.budget_limits.end_date')}
                placeholder={i18n.t('budgets.forms.new_budget.budget_limits.end_date_placeholder')}
              />
            </Box>
          </Form>
        </Box>
        <Box width="s300">
          <Alert color="info">
            <Text.Normal>{i18n.t('budgets.forms.new_budget.budget_limits.hint')}</Text.Normal>
          </Alert>
        </Box>
      </Box>
    </Box>
  )
}

export const budgetAmountsStep = (): WizardStep<WizardData> => ({
  title: i18n.t('budgets.forms.new_budget.budget_amounts.title'),
  nextText: i18n.t('common.submit'),
  render: (props) => <BudgetAmounts {...props} />,
  isCompleted: () => true,
})
